import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ESRState } from "../types/data";
import { ESRThunkDispatch, listESRs, UPDATE_ESR_QUERY, RESET_ESR_QUERY, RESET_IS_USER_PERMITTED_TO_VIEW_ESR } from "../actions/form";
import {
	Box,
	Button,
	InputBase,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	Tooltip,
	Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { SearchOutlined } from "@mui/icons-material";
import { FacetFilter } from "./FacetFilter";
import { StatusFacetFilter } from "./StatusFacetFilter";
import { GROUPS } from "../reviewStatus";
import { DataAction } from "../types/action";
import { useCurrentUser } from "../hooks/useCurrentUser";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

type Order = "asc" | "desc";

interface ESRTableData {
	id: string;
	latest_addendum_id: string;
	latest_addendum_label: string;
	sponsor_type: string;
	submitted: boolean;
	submission_status: string;
	updated: string;
	// phase: any;
	contract_no: string;
	section_no: string;
	counties: string;
	route: string;
	from_to: string;
	cultural_status: StatusLine[];
	natural_status: StatusLine[];
	regulated_status: StatusLine[];
}

interface StatusLine {
	group: string;
	text: string;
	color: string;
}

const getStatusLines = (statusMap: Record<string, any> | null): StatusLine[] => {
	const lines: StatusLine[] = [];
	for (const [key, group] of GROUPS) {
		const status = statusMap?.[key];
		if (!status) continue;
		lines.push({ group, text: status.text, color: status.color });
	}
	return lines;
};

// Darker shades (same hue) of the stored status colors, easier to read as text
const STATUS_TEXT_COLOR: Record<string, string> = {
	orange: "#B37400",
	red: "#CC0000",
	green: "#006400",
	grey: "#616161",
};

const StatusLines = ({ lines }: { lines: StatusLine[] }) => (
	<Stack direction="column" gap="4px">
		{lines.map((line) => (
			<Box key={line.group}>
				{line.group}: <span style={{ color: STATUS_TEXT_COLOR[line.color] ?? line.color }}>{line.text}</span>
			</Box>
		))}
	</Stack>
);

interface EnhancedTableProps {
	order: Order;
	orderBy: string;
	onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
}

interface HeadCell {
	key: string;
	label: string;
	numeric: boolean;
}

const headCells: readonly HeadCell[] = [
	{
		key: "id",
		numeric: false,
		label: "Seq. No.",
	},
	{
	  key: "addendum",
	  numeric: false,
	  label: "Addendum",
	},
	{
		key: "sponsor_type",
		numeric: false,
		label: "Agency",
	},
	{
		key: "identifiers__contract_no",
		numeric: false,
		label: "Contract No.",
	},
	{
		key: "identifiers__section_no",
		numeric: false,
		label: "Section No.",
	},
	{
		key: "identifiers__counties",
		numeric: false,
		label: "Counties",
	},
	{
		key: "identifiers__route",
		numeric: false,
		label: "Route",
	},
	{
		key: "identifiers__from_to",
		numeric: false,
		label: "From-To",
	},
	{
		key: "submitted",
		numeric: false,
		label: "Submission Status",
	},
	{
		key: "cultural_status",
		numeric: false,
		label: "Cultural Resource Unit",
	},
	{
		key: "natural_status",
		numeric: false,
		label: "Natural Resource Unit",
	},
	{
		key: "regulated_status",
		numeric: false,
		label: "Regulated Substances Unit",
	},
	{
		key: "updated",
		numeric: false,
		label: "Updated",
	},
];

function EnhancedTableHead(props: EnhancedTableProps) {
	const { order, orderBy, onRequestSort } = props;
	const createSortHandler =
		(property: string) => (event: React.MouseEvent<unknown>) => {
			onRequestSort(event, property);
		};

	return (
		<TableHead
			sx={{ position: "sticky", top: 0, background: "rgb(250, 251, 252)" }}
		>
			<TableRow>
				{headCells.map((headCell) => (
					<TableCell
						sx={{ whiteSpace: "nowrap", borderBottom: "none" }}
						key={headCell.key}
						align={headCell.numeric ? "right" : "left"}
						sortDirection={orderBy === headCell.key ? order : false}
					>
						{headCell.label !== 'Addendum' &&
							<TableSortLabel
								active={orderBy === headCell.key}
								direction={orderBy === headCell.key ? order : "asc"}
								onClick={createSortHandler(headCell.key || headCell.key)}
							>
								{headCell.label}
							</TableSortLabel>
						}
						{
							headCell.label === 'Addendum' && headCell.label
						}
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
}

const ROW_HEIGHT = 80;

export const ESRList = ({ testOnly = false }: { testOnly?: boolean } = {}): JSX.Element => {
	const dispatch = useDispatch<ESRThunkDispatch>();

	const navigate = useNavigate();

	const [user, setUser] = useCurrentUser();
	const [searchString, setSearchString] = useState("");
	const timerRef = useRef(-1);
	useEffect(() => {
		window.clearTimeout(timerRef.current);
		timerRef.current = window.setTimeout(() => {
			dispatch({
				type: "UPDATE_ESR_QUERY",
				updates: {
					search: searchString,
					page: 0,
				},
			});
		}, 100);
	}, [searchString]);

	const handleSearch = () => {
		dispatch({
			type: "UPDATE_ESR_QUERY",
			updates: {
				search: searchString,
				page: 0,
			},
		});
		if(user){
			dispatch(listESRs(user.email, testOnly));
		}
	};


	const currentPage = useSelector((state: ESRState) => {
		return state.esr_list_current_page.map<ESRTableData>((esr: any) => {
			return {
				id: esr.id,
				latest_addendum_id: esr.latest_addendum_id,
				latest_addendum_label: esr.latest_addendum_label,
				sponsor_type: esr.sponsor_type,
				description: esr.identifiers?.["description"],
				contract_no: esr.identifiers?.["contract_no"],
				section_no: esr.identifiers?.["section_no"],
				project_no: esr.identifiers?.["project_no"],
				counties: esr.identifiers?.["counties"],
				route: esr.identifiers?.["route"],
				from_to: esr.identifiers?.["from_to"],
				submission_status: esr.identifiers?.["submission_status"],
				updated: esr.updated,
				submitted: esr?.["submission_status"],
				cultural_status: getStatusLines(esr.cultural_status),
				natural_status: getStatusLines(esr.natural_status),
				regulated_status: getStatusLines(esr.regulated_status),
			};
		});
	});

	// table states
	const facets = useSelector((s: ESRState) => s.esr_facets);
	const query = useSelector((s: ESRState) => s.esr_query);
	const order = useSelector((s: ESRState) => {
		// If `order_by` is not specified, the list is ordered by ID. The default order for ID is descending.
		const defaultOrder = s.esr_query["order_by"] ? "asc" : "desc";
		return s.esr_query["order"] || defaultOrder;
	});
	const orderBy = useSelector((s: ESRState) => s.esr_query["order_by"] ?? "id");
	const page = useSelector((s: ESRState) => s.esr_query["page"] ?? 0);
	const pageSize = useSelector((s: ESRState) => s.esr_query["page_size"] ?? 10);
	const total = useSelector((s: ESRState) => s.esr_total_count);

	const handleRequestSort = (event: React.MouseEvent<unknown>, key: string) => {
		const isAsc = orderBy === key && order === "asc";
		dispatch<DataAction>({
			type: UPDATE_ESR_QUERY,
			updates: {
				order: isAsc ? "desc" : "asc",
				order_by: key,
				page: 0,
			},
		});
	};

	const handleChangePage = (event: unknown, newPage: number) => {
		dispatch<DataAction>({
			type: UPDATE_ESR_QUERY,
			updates: {
				page: newPage,
			},
		});
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		dispatch<DataAction>({
			type: UPDATE_ESR_QUERY,
			updates: {
				page_size: parseInt(event.target.value, 10),
				page: 0,
			},
		});
	};

	// use effect to reset the filter selections
	useEffect(()=>{
		dispatch<DataAction>({
			type: RESET_ESR_QUERY,
		});
		dispatch<DataAction>({
			type: RESET_IS_USER_PERMITTED_TO_VIEW_ESR,
		});
	}, []);

	const openESR = (esrId: string, activeLabel: string, activeId: string, submitted: boolean) => {
		if (Boolean(submitted)) {
			navigate(`/esr/${esrId}/${activeLabel}/${activeId}`);
		} else {
			navigate(`/esr/${esrId}/${activeLabel}/${activeId}/0`);
		}
	};

	// Avoid a layout jump when reaching the last page with empty rows.
	const emptyRows = page > 0 ? Math.max(0, (1 + page) * pageSize - total) : 0;

	const queryString = useSelector((s: ESRState) => {
		const params = new URLSearchParams();
		for (const [key, value] of Object.entries(s.esr_query)) {
			if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) continue;
			params.set(key, String(value));
		}
		return params.toString();
	});
	useEffect(() => {
		if (user) {
			dispatch(listESRs(user.email, testOnly));
		}
	}, [user, queryString]);

	// useEffect(() => {
	// 	if (user) {
	// 		dispatch(listAllUnresolvedAndUnreadThreads());
	// 	}
	// }, [user]);

	// Temporary workaround to silence WAVE
	const paginationRef = useRef<HTMLDivElement | null>(null);
	useEffect(() => {
		paginationRef.current?.querySelectorAll("input").forEach((el) => {
			el.setAttribute("aria-label", "pagination");
		});
	});

	/**
	 * Initially, load list of ERSs the user has access, with Load button for each
	 * New ESR button that creates a new entry and navigates to it
	 */

	return (
		<Stack direction="column" sx={{ height: "100%" }}>
			<Box
				sx={{
					boxSizing: "border-box",
					p: "60px 50px 50px",
					background: "rgb(250, 251, 252)",
					flex: "none",
					boxShadow: "0 2px 2px 0 #00000011",
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Typography fontSize={24} fontWeight={700}>
						All Projects
					</Typography>
					<Box sx={{ flex: "auto" }} />
					<Paper
						square
						elevation={0}
						sx={{
							p: "2px 4px",
							display: "flex",
							alignItems: "center",
							width: 400,
							bgcolor: "#0000000A",
						}}
					>
						<InputBase
							sx={{ 
								ml: 2, 
								flex: 1,
								"& input::placeholder": {
									color: "#4a4a4a",
									opacity: 1,
								},
							}}
							value={searchString}
							onChange={(e) => setSearchString(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									handleSearch();
								}
							}}
							placeholder="Search by Seq. #, Contract #, Sec. #, or County"
						/>
						<SearchOutlined />
					</Paper>
					<Button
							variant="contained"
							size="small"
							onClick={handleSearch}
							sx={{ mr: 1 }}
						>
							Find
					</Button>
				</Box>
				<Stack
					direction="row"
					gap="12px"
					flexWrap="wrap"
					alignItems="center"
					sx={{ mt: "40px", mb: "30px" }}
				>
					{facets.map(({ key, label, type, values }, i) => {
						const Filter = type === "status" ? StatusFacetFilter : FacetFilter;
						return [
							i > 0 && (
								<Typography
									key={i}
									color="gray"
									fontWeight="semibold"
									fontSize={12}
								>
									AND
								</Typography>
							),
							<Filter
								key={key}
								label={label}
								value={query[key] ?? []}
								options={values}
								onChange={(value) => {
									dispatch({
										type: UPDATE_ESR_QUERY,
										updates: {
											[key]: value,
											page: 0,
										},
									});
								}}
							/>,
						];
					})}
				</Stack>
			</Box>
			<Box
				sx={{ mx: "50px", mt: "-58px", mb: "50px", flex: "auto", minHeight: 0 }}
			>
				<TableContainer sx={{ height: "100%" }}>
					<Table sx={{ minWidth: 750 }}>
						<EnhancedTableHead
							order={order}
							orderBy={orderBy}
							onRequestSort={handleRequestSort}
						/>
						<TableBody>
							{currentPage.map((row, index) => (
								<TableRow
									hover
									key={index}
									sx={{ cursor: "pointer", height: ROW_HEIGHT }}
									onClick={() => openESR(row.id, row.latest_addendum_label, row.latest_addendum_id, row.submitted)}
								>
									<TableCell align="left">{row.id}</TableCell>
									<TableCell align="left">{row.latest_addendum_label}</TableCell>
									<TableCell align="left">{row.sponsor_type}</TableCell>
									<TableCell align="left">
										{typeof row.contract_no === "string" &&
										row.contract_no.startsWith("[")
											? JSON.parse(row.contract_no).join(", ")
											: row.contract_no}
									</TableCell>
									<TableCell align="left">{row.section_no}</TableCell>
									<TableCell align="left">{row.counties}</TableCell>
									<TableCell align="left">{row.route}</TableCell>
									<TableCell align="left">
										<Tooltip title={row.from_to} arrow>
											<span
											style={{
												display: "inline-block",
												maxWidth: "180px",
												whiteSpace: "nowrap",
												overflow: "hidden",
												textOverflow: "ellipsis",
												verticalAlign: "bottom",
											}}
											>
											{row.from_to}
											</span>
										</Tooltip>
									</TableCell>
									<TableCell align="left">{row.submitted === true ? <CheckCircleIcon style={{ color: "green" }} /> : ""}</TableCell>
									<TableCell align="left" sx={{ minWidth: 280 }}>
										<StatusLines lines={row.cultural_status} />
									</TableCell>
									<TableCell align="left" sx={{ minWidth: 280 }}>
										<StatusLines lines={row.natural_status} />
									</TableCell>
									<TableCell align="left" sx={{ minWidth: 280 }}>
										<StatusLines lines={row.regulated_status} />
									</TableCell>
									<TableCell align="left">
										{row.updated.substring(0, 10)}
									</TableCell>
								</TableRow>
							))}
							{emptyRows > 0 && (
								<TableRow style={{ height: ROW_HEIGHT * emptyRows }}>
									<TableCell colSpan={6} />
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					slotProps={{
						select: {
							inputProps: {
								hidden: true,
							},
						},
					}}
					ref={paginationRef}
					rowsPerPageOptions={[5, 10, 20]}
					component="div"
					count={total}
					rowsPerPage={pageSize}
					page={page}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			</Box>
		</Stack>
	);
};
